import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { useSearchParams } from 'react-router-dom';
import { MessageSquare } from 'lucide-react';
import ChatList from '../components/ChatList';
import ChatRoom from '../components/ChatRoom';
import Sidebar from '../components/Sidebar';
import { chatService } from '../services/chatService';

const Chat = () => {
  const { userData } = useAuth();
  const { t } = useLanguage();
  const [searchParams] = useSearchParams();
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [tab, setTab] = useState('all'); // 'all' | 'direct' | 'group'

  // Handle URL parameter for opening specific conversation
  useEffect(() => {
    const conversationId = searchParams.get('conversation');
    if (conversationId) {
      const loadConversation = async () => {
        try {
          const conversation = await chatService.getConversation(conversationId);
          if (conversation) {
            setSelectedConversation(conversation);
          }
        } catch (error) {
          console.error('Error loading conversation:', error);
        }
      };
      loadConversation();
    }
  }, [searchParams]);

  const handleSelectConversation = (conversation) => {
    // Only update if it's actually a different conversation
    if (selectedConversation?.id !== conversation?.id) {
      setSelectedConversation(conversation);
    }
  };

  const handleBackToList = () => {
    setSelectedConversation(null);
  };

  const handleTabChange = (newTab) => {
    if (tab !== newTab) {
      setTab(newTab);
      // Reset selected conversation when changing tabs to prevent glitching
      setSelectedConversation(null);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="flex">
        <Sidebar />
        <div className="flex-1 ml-72 -ml-8">
          <header className="bg-white border-b border-slate-200 sticky top-0 z-20 w-full">
            <div className="flex items-center justify-between px-8 py-5">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center">
                  <MessageSquare className="w-5 h-5 text-slate-600" strokeWidth={1.75} />
                </div>
                <div>
                  <h1 className="text-xl font-semibold text-slate-800">
                    {t('dashboard.owner.pages.chats.title')}
                  </h1>
                  <p className="text-sm text-slate-500 mt-0.5">
                    {t('dashboard.owner.pages.chats.subtitle')}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="text-right text-sm">
                  <div className="font-medium text-slate-700">
                    {new Date().toLocaleDateString('en-US', { weekday: 'short' })}
                  </div>
                  <div className="text-xs text-slate-500">
                    {new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                  </div>
                </div>
                <div className="w-9 h-9 bg-slate-600 rounded-lg flex items-center justify-center">
                  <span className="text-white font-medium text-sm uppercase">
                    {userData?.displayName?.charAt(0) || 'U'}
                  </span>
                </div>
              </div>
            </div>
          </header>

          <div className="p-6">
            <div className="max-w-7xl mx-auto">
              <div className="bg-white rounded-lg shadow-sm border border-slate-200 h-[calc(100vh-11rem)] flex flex-col overflow-hidden">
                <div className="px-5 py-4 border-b border-slate-200 bg-slate-50/50">
                  <div className="flex items-center justify-between">
                    <div className="inline-flex rounded-md border border-slate-200 bg-white p-0.5">
                      <button
                        className={`px-4 py-2 text-sm font-medium rounded transition-colors ${
                          tab === 'all'
                            ? 'bg-slate-700 text-white'
                            : 'text-slate-600 hover:text-slate-800 hover:bg-slate-100'
                        }`}
                        onClick={() => handleTabChange('all')}
                      >
                        All Messages
                      </button>
                      <button
                        className={`px-4 py-2 text-sm font-medium rounded transition-colors ${
                          tab === 'direct'
                            ? 'bg-slate-700 text-white'
                            : 'text-slate-600 hover:text-slate-800 hover:bg-slate-100'
                        }`}
                        onClick={() => handleTabChange('direct')}
                      >
                        Direct
                      </button>
                      <button
                        className={`px-4 py-2 text-sm font-medium rounded transition-colors ${
                          tab === 'group'
                            ? 'bg-slate-700 text-white'
                            : 'text-slate-600 hover:text-slate-800 hover:bg-slate-100'
                        }`}
                        onClick={() => handleTabChange('group')}
                      >
                        Group Chats
                      </button>
                    </div>
                    <span className="text-xs text-slate-500">Real-time messaging</span>
                  </div>
                </div>

                <div className="flex flex-1 overflow-hidden">
                  <ChatList
                    onSelectConversation={handleSelectConversation}
                    selectedConversationId={selectedConversation?.id}
                    typeFilter={tab === 'all' ? 'all' : tab}
                    key={`chatlist-${tab}`} // Force re-render when tab changes
                  />
                  <ChatRoom
                    conversation={selectedConversation}
                    onBack={handleBackToList}
                    key={`chatroom-${selectedConversation?.id || 'empty'}`} // Force re-render when conversation changes
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Chat;
